import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import type { AssignedClient } from '../contexts/ActiveClientContext';

/*
 * The assigned-client picker in the partner top bar. A partner with twenty
 * clients cannot scan a native select, so this is a button that opens a list
 * with a search box: type a few letters, arrow down, Enter. Escape or a click
 * outside closes it without changing the selection.
 */

const matches = (client: AssignedClient, needle: string): boolean => {
  if (!needle) return true;
  return client.name.toLowerCase().includes(needle);
};

export const ClientSwitcher: React.FC<{
  clients: AssignedClient[];
  active: AssignedClient | null;
  loading: boolean;
  onChange: (id: string) => void;
}> = ({ clients, active, loading, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const needle = query.trim().toLowerCase();
  const rows = useMemo(() => clients.filter((c) => matches(c, needle)), [clients, needle]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(Math.max(0, clients.findIndex((c) => c.id === active?.id)));
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, clients, active?.id]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [needle]);

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(rows.length - 1, c + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
      return;
    }
    if (e.key === 'Enter' && rows[cursor]) {
      e.preventDefault();
      pick(rows[cursor].id);
    }
  };

  const label = (): string => {
    if (loading) return 'Loading clients…';
    if (clients.length === 0) return 'No assigned clients';
    if (active) return active.name;
    return 'Choose a client';
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading || clients.length === 0}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 max-w-[150px] items-center gap-2 sm:max-w-[260px] rounded-act border border-line-strong bg-white px-3 text-[13px] font-semibold text-ink disabled:opacity-60"
      >
        <span className="truncate">{label()}</span>
        <ChevronDown size={14} className="shrink-0 text-ink-soft" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose the assigned client"
          className="absolute left-0 top-12 z-[90] w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-panel border border-line bg-white shadow-aa-lg"
        >
          <label className="flex items-center gap-2 border-b border-line-soft px-3">
            <Search size={14} className="shrink-0 text-ink-soft" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Find a client"
              aria-label="Find a client"
              className="h-11 w-full bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-soft"
            />
          </label>
          <ul role="listbox" aria-label="Assigned clients" className="max-h-[320px] overflow-y-auto py-1">
            {rows.length === 0 && (
              <li className="px-3 py-3 text-[13px] text-ink-dim">No client matches “{query.trim()}”.</li>
            )}
            {rows.map((client, index) => {
              const selected = client.id === active?.id;
              return (
                <li key={client.id} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => pick(client.id)}
                    className={clsx(
                      'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13px]',
                      index === cursor && 'bg-canvas-inset',
                      selected && 'font-semibold text-ink',
                      !selected && 'text-ink-muted',
                    )}
                  >
                    <span className="truncate">{client.name}</span>
                    {selected && <Check size={14} className="shrink-0 text-ink" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
